import { useState, useEffect, useRef } from "react"
import { Form, Row, Col } from "react-bootstrap"
import { useNavigate, Link } from "react-router-dom"
import { useQuery, useLazyQuery, useMutation } from "@apollo/client"
import CustomModal from '../../../../components/CustomModal'
import CustomButton from '../../../../components/CustomButton'
import useUser from "../../../../hooks/useUser"

import "./index.scss"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import GET_COURSES from "../../../../graphql/queries/courseCatalogue/getCourses"
import GET_COURSE_FORM from "../../../../graphql/queries/courseCatalogue/getCourseForm"

import CREATE_COURSE from '../../../../graphql/mutations/courseCatalogue/createCourse'
import EDIT_COURSE from '../../../../graphql/mutations/courseCatalogue/editCourse'
import REMOVE_COURSE from '../../../../graphql/mutations/courseCatalogue/removeCourse'

import CustomTable from "../Table"
import TableSkeleton from "../Table/components/TableSkeleton"

import toastConfiguration from "../../../../data/toastConfiguration"
import { ToastContainer, toast } from "react-toastify"

import IconButton from "../../../../components/IconButton"
import {
  faPlus,
  faCheck,
  faFloppyDisk,
  faTrash,
  faChevronLeft,
  faUpload
} from "@fortawesome/free-solid-svg-icons"
import COLORS from "../../../../data/colors"

import { useTranslation } from 'react-i18next'

import DynamicForm from '../../../../components/DynamicForm'

import { 
  convertCourseStringToNumbers, 
  isDeepEqual, 
  getPatchDiff 
} from '../../../../utils/courseCatalogueManager'


const Courses = () => {

  const formRef = useRef(null)
  const [loadingComponent, setLoadingComponent] = useState(true)
  const [courseData, setCourseData] = useState([])
  const [courseForm, setCourseForm] = useState([])
  const [selectedCourse, setSelectedCourse] = useState({})
  const [tableHeaders, setTableHeaders] = useState([])
  const [columnsPerFieldMap, setColumnsPerFieldMap] = useState({})
  const [formData, setFormData] = useState({})
  const [validated, setValidated] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedAction, setSelectedAction] = useState("")

  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useUser()

  const userHasPermission = user?.permissions?.some(
    (item) => item.value === "manageCC"
  )

  const [loadCourses, { 
    loading: loadingCourses, 
    data: dataCourses,
    error: errorCourses,
    refetch: refetchCourses }
  ] = useLazyQuery(GET_COURSES, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      console.log("Courses data received")

      setCourseData(data.coursesCC)
    },
    onError: (error) => {
      console.error("Error loading Courses:", error.message)
    }
  })

  const [loadGetCourseForm, { 
    loading: loadingGetCourseForm, dataGetCourseForm, errorGetCourseForm }
  ] = useLazyQuery(GET_COURSE_FORM, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      console.log("Course Form data received")

      // Save form into session storage
      sessionStorage.setItem("courseForm", JSON.stringify(data.getCourseForm))

      setCourseForm(data.getCourseForm)
      
      // Get table headers list from form display
      const courseFormArray = Object.entries(data.getCourseForm)
      const headers = []
      courseFormArray.forEach(formItem => {
        if(formItem[1]?.display?.column_display) {
          headers.push(formItem[0])
        }
      })

      // Create columnsPerFieldMap
      const columnsHeaders = headers.reduce((acc, key) => {
        acc[key] = "col-" + headers.length
        return acc
      }, {})

      setColumnsPerFieldMap(columnsHeaders)
      setTableHeaders(headers)

    },
    onError: (error) => {
      console.error("Error loading Course Form:", error.message)
    }
  })

  const [callCreateCourse, { 
    loading: loadingCreateCourse, dataCreateCourse }
  ] = useMutation(CREATE_COURSE, {
    onCompleted: (data) => {
      console.log("Course created")
    },
    onError: (error) => {
      console.error("Error creating Course:", error)
    }
  })

  const [callEditCourse, { 
    loading: loadingEditCourse, dataEditCourse }
  ] = useMutation(EDIT_COURSE, {
    onCompleted: (data) => {
      console.log("Course modified")
    },
    onError: (error) => {
      console.error("Error editing Course:", error)
    }
  })

  const [callRemoveCourse, { 
    loading: loadingRemoveCourse, dataRemoveCourse }
  ] = useMutation(REMOVE_COURSE, {
    onCompleted: (data) => {
      console.log("Course removed")
    },
    onError: (error) => {
      console.error("Error removing Course:", error)
    }
  })

  useEffect(() => {

    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const heiConnectionStorage = JSON.parse(localStorage.getItem('heiConnection'))

    if(!userHasPermission) {
      navigate('/cc')
      return
    }

    // If userId and backend username don't match, redirects to /cc
    if(user?.userId !== userConnectionStorage?.backendUserName) {
      navigate('/cc')
      return
    }

    // If there are no connections redirects to /cc
    if(!heiConnectionStorage || !userConnectionStorage) {
      navigate('/cc')
      return
    }

    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey
    const backendHeiUuid = heiConnectionStorage?.backendHeiUuid

    const courseForm = JSON.parse(sessionStorage.getItem('courseForm'))

    loadCourses({
      variables: {
        schacCode: user?.heiID,
        baseUrl,
        apiKey
      }
    })

    if(!courseForm){
      loadGetCourseForm({
        variables: { 
          baseUrl, 
          apiKey,
          heiId: user?.heiID,
        }
      })
    } else {
      setCourseForm(courseForm)

      // Get table headers list from form display
      const courseFormArray = Object.entries(courseForm)
      const headers = []
      
      courseFormArray.forEach(formItem => {
        if(formItem[1]?.display?.column_display) {
          headers.push(formItem[0])
        }
      })

      // Create columnsPerFieldMap
      const columnsHeaders = headers.reduce((acc, key) => {
        acc[key] = "col-" + headers.length
        return acc
      }, {})

      setColumnsPerFieldMap(columnsHeaders)
      setTableHeaders(headers)
      /* setLoadingComponent(false) */
    }

  }, [loadCourses])

  useEffect(() => {
    if (dataCourses?.coursesCC && courseForm && Object.keys(courseForm).length > 0) {
      /* setCourseData(dataCourses.coursesCC) */
      setLoadingComponent(false)
    }
  }, [dataCourses, courseForm])

  const handleFormChange = (data) => {
    setFormData(data)
  }

  const formIsValidated = (e) => {
    setValidated(true)
    e.preventDefault()

    if (!formRef.current.checkValidity()) {
      e.stopPropagation()
      return false
    }

    return true
  }

  const handleAdd = async (e) => {

    if (!formIsValidated(e)) return

    const toastId = toast.loading('Creating Course', toastConfiguration)

    const heiConnectionStorage = JSON.parse(localStorage.getItem('heiConnection'))
    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey
    const backendHeiUuid = heiConnectionStorage?.backendHeiUuid

    const cleanedData = convertCourseStringToNumbers(formData)

    try {
      
      let result = {}

      result = await callCreateCourse({
        variables: {
          courseData: cleanedData,
          baseUrl: baseUrl,
          apiKey: apiKey,
          heiUuid: backendHeiUuid
        }
      })

      setValidated(false)
      if (!result?.errors) {

        toast.update(toastId, {
          ...toastConfiguration,
          render: `Created Course ${cleanedData.title[0].string}`,
          type: "success",
          isLoading: false,
        })
        const { data: refreshedCourse } = await refetchCourses({
          variables: { baseUrl, apiKey }
        })

        setCourseData(refreshedCourse.coursesCC)

        setShowModal(false)

      } else {

        console.error("Create Course failed.")

        toast.update(toastId, {
          ...toastConfiguration,
          render: "Could not create Course. " + result?.errors,
          type: "error",
          isLoading: false,
        })
      }

    } catch (err) {
      console.error("Create Course failed:", err)

      toast.update(toastId, {
        ...toastConfiguration,
        render: "Could not create Course. Try again later.",
        type: "error",
        isLoading: false,
      })
    }

  }

  const handleEdit = async (e) => {

    if (!formIsValidated(e)) return

    const toastId = toast.loading('Updating Course', toastConfiguration)

    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const heiConnectionStorage = JSON.parse(localStorage.getItem('heiConnection'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey
    const backendHeiUuid = heiConnectionStorage?.backendHeiUuid

    let cleanedData = convertCourseStringToNumbers(formData)
    cleanedData.id = selectedCourse.id
    cleanedData.uuid = selectedCourse.uuid
    cleanedData.status = selectedCourse.status

    // Check if the data has changed
    // If yes, run the query
    // if not, show an error message
    if(!isDeepEqual(selectedCourse, cleanedData)) {
      try {
        let result = {}

        result = await callEditCourse({
          variables: {
            courseData: getPatchDiff(selectedCourse, cleanedData),
            baseUrl,
            apiKey,
            heiUuid: backendHeiUuid,
          }
        })

        setValidated(false)
        if (!result?.errors) {

          toast.update(toastId, {
            ...toastConfiguration,
            render: `Course ${cleanedData.title[0].string} updated`,
            type: "success",
            isLoading: false,
          })
          const { data: refreshedCourse } = await refetchCourses({
            variables: { 
              baseUrl, 
              apiKey,
              heiUuid: backendHeiUuid 
            }
          })

          setCourseData(refreshedCourse.coursesCC)

          setShowModal(false)

        } else {

          console.error("Edit Course failed.")

          toast.update(toastId, {
            ...toastConfiguration,
            render: "Could not create Course. " + result?.errors,
            type: "error",
            isLoading: false,
          })
        }

      } catch (err) {
        console.error("Edit Course failed:", err)

        toast.update(toastId, {
          ...toastConfiguration,
          render: "Could not edit Course. Try again later.",
          type: "error",
          isLoading: false,
        })
      }
    } else {

      toast.update(toastId, {
        ...toastConfiguration,
        render: "There are no changes in the data.",
        type: "error",
        isLoading: false,
      })
    }

  }

  const handleDelete = async (e) => {
    
    setShowModal(false)

    const backendInstanceStorage = JSON.parse(localStorage.getItem('backendInstance'))
    const userConnectionStorage = JSON.parse(localStorage.getItem('userConnection'))
    const baseUrl = backendInstanceStorage?.baseUrl
    const apiKey = userConnectionStorage?.backendUserApiKey

    const toastId = toast.loading('Archiving Course...', toastConfiguration)
    
    try {
      const result = await callRemoveCourse({
       variables: {
          baseUrl: baseUrl,
          apiKey: apiKey,
          courseUuid: selectedCourse.id,
        }
      })

      setValidated(false)
      
      if (!result?.errors) {
        toast.update(toastId, {
          ...toastConfiguration,
          render: 'Successfully archived the Course',
          type: "success",
          isLoading: false,
        })
        const { data: refreshedCourse } = await refetchCourses({
          variables: { baseUrl, apiKey }
        })

        setCourseData(refreshedCourse.coursesCC)
        
        setShowModal(false)

      } else {

        console.error("Archive Course failed.")

        toast.update(toastId, {
          ...toastConfiguration,
          render: "Could not archive Course. " + result?.errors,
          type: "error",
          isLoading: false,
        })
      }
    } catch (err) {

      toast.update(toastId, {
        ...toastConfiguration,
        render: 'Could not archive the Course',
        type: "error",
        isLoading: false,
      })

    }
  }

  return (
    <main className="h-auto text-center mt-4">
      {loadingCourses || loadingGetCourseForm || loadingComponent ? <TableSkeleton
        columnsPerFieldMap={columnsPerFieldMap}
      /> : errorCourses ?
        <div className="text-center text-danger mx-auto">{t('errorMessage')}</div> :
        <>
          <Row className="my-4 display-flex justify-content-between">
            <Col md={2} className="my-4">
              <Link to="/cc" className="goBackLink">
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  color={COLORS.greyText}
                  className="with-icon me-1"
                  id={"smallIcon"}
                />
                {"Go back"}
              </Link>
            </Col>
            <Col md={4} className="my-2">
              <CustomButton 
                buttonType={"primary"} 
                small={true} 
                text={"upload"} 
                hasIcon={true} 
                iconColor={COLORS.white} 
                icon={faUpload} 
                handleClick={(e) => { navigate("/cc/courses/upload") }}
              />
              <CustomButton 
                type="submit" 
                buttonType={"primary"} 
                text={'add a new course'} 
                icon={faPlus} 
                handleClick={() => {
                  setSelectedAction("add")
                  setValidated(false)
                  setShowModal(true)
                }} 
                hasIcon 
                styles={{ marginLeft: 20 }} 
                small 
              />
            </Col>
          </Row>
          <CustomTable
            type={"courses-"}
            columnsPerFieldMap={columnsPerFieldMap}
            hasPagination={false}
            hasFilters={false}
            rowClassName="courses-listing-table-row"
            headerClassName="courses-listing-table-header"
            data={dataCourses?.coursesCC ?? []}
            tableHead={tableHeaders}
            onClickHandlers={dataCourses?.coursesCC.map(
              ( course ) =>
              (e) => {
                navigate("/cc/courses/" + course.id)
              }
            )}
          >
            {dataCourses?.coursesCC?.map((course) => (
              <div
                key={course?.id}
                className={`py-3 px-2 justify-content-end justify-content-lg-around align-items-center bg-none bg-md-light button-triplet-requests flex-grow-1 d-flex gap-3 gap-lg-1`}
              >
                {/* <Toggle/> */}
                <IconButton
                  variant="edit"
                  title={'Save Changes'}
                  onClick={() => {
                    setSelectedAction("edit")
                    setShowModal(true)
                    setSelectedCourse(course)
                  }}
                />
                <IconButton
                  variant="delete"
                  onClick={() => {
                    setSelectedAction("delete")
                    setShowModal(true)
                    setSelectedCourse(course)
                  }}
                  title={'Delete'}
                />
              </div>
            ))}
          </CustomTable>
          <CustomModal
            show={showModal}
            handleClose={() => {
              setShowModal(false)
              setValidated(false)
            }}
            disabledCancelButton={
              loadingCreateCourse || loadingEditCourse
            }
            body={
              (selectedAction === "add" || selectedAction === "edit") && (
                <div>
                  
                  <Form ref={formRef} noValidate validated={validated}>
                    <DynamicForm 
                      schema={courseForm} 
                      onChange={handleFormChange}
                      defaultValues={selectedAction === 'edit' ? selectedCourse : {}} 
                    />
                  </Form>
                  
                </div>
              )
            }
            button={
              selectedAction === "add" ? ( // new
                <CustomButton
                  buttonType={"primary"}
                  disabled={loadingCreateCourse}
                  text={"Add"}
                  icon={faCheck}
                  hasIcon={true}
                  iconColor={COLORS.white}
                  handleClick={handleAdd}
                />
              ) : selectedAction === "edit" ? ( // edit
                <CustomButton
                  buttonType={"primary"}
                  disabled={loadingEditCourse}
                  text={'Save Changes'}
                  icon={faFloppyDisk}
                  hasIcon={true}
                  iconColor={COLORS.white}
                  handleClick={handleEdit}
                />
              ) : (
                // delete
                <CustomButton
                  buttonType={"decline"}
                  text={'Archive'}
                  iconColor={COLORS.white}
                  handleClick={handleDelete}
                />
              )
            }
            title={
              selectedAction === "add" ? (
                'Add a new Course'
              ) : selectedAction === "edit" ? (
                'Edit an existing Course'
              ) : (
                <>
                  {'You are about to '}
                  <span className="accounts-and-access-delete-text-on-modal">
                    {'archive'}
                  </span>{" "}
                  {'a Course'}
                </>
              )
            }
            subtitle={
              selectedAction === "add" ? (
                'Add the necessary information to add a new Course'
              ) : selectedAction === "edit" ? (
                'Add the necessary information to edit a new Course'
              ) : ('The Course will no longer be visible but the record will be kept. This action cannot be undone.')
            }
            hasIcon
            fullscreen
          />
        </>}
        <ToastContainer
          position="bottom-left"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
    </main>
  )
}

export default Courses
